import React, { useRef, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { Button } from 'antd';
import { ZoomInOutlined, ZoomOutOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

export interface Highlight {
  id: string;
  page: number; // 1-based page number
  bounds: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  phrase: string;
  code?: string;
  color?: string;
}

interface PdfViewerProps {
  fileUrl: string;
  highlights?: Highlight[];
  currentHighlightIndex?: number;
  onHighlightClick?: (highlight: Highlight) => void;
  onDocumentLoad?: () => void;
  onContentSizeChange?: (width: number, height: number) => void;
}

const PdfViewer = forwardRef<any, PdfViewerProps>(({
  fileUrl,
  highlights = [],
  currentHighlightIndex = 0,
  onHighlightClick,
  onDocumentLoad,
  onContentSizeChange,
}, ref) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [pageDimensions, setPageDimensions] = useState<{ [key: number]: { width: number; height: number } }>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const resizeObservers = useRef<{ [key: number]: ResizeObserver | null }>({});

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    goToHighlight: (highlight: Highlight) => {
      console.log('goToHighlight called with:', highlight);
      if (highlight) {
        // Navigate to the page
        setCurrentPage(highlight.page);
        
        // Scroll to the highlight
        setTimeout(() => {
          const pageElement = pageRefs.current[highlight.page];
          if (pageElement) {
            pageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }
    }
  }));

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    console.log('PDF loaded successfully, pages:', numPages);
    setNumPages(numPages);
    if (onDocumentLoad) {
      onDocumentLoad();
    }
  };

  const onDocumentLoadError = (error: Error) => {
    console.error('Failed to load PDF:', error);
  };

  const updatePageDimensions = (pageNum: number) => {
    const pageElement = pageRefs.current[pageNum];
    if (!pageElement) return;

    // Find the canvas element inside the page container
    const canvas = pageElement.querySelector('canvas.react-pdf__Page__canvas') as HTMLCanvasElement;
    if (canvas) {
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      if (width > 0 && height > 0) {
        setPageDimensions(prev => {
          const current = prev[pageNum];
          // Only update if dimensions actually changed to avoid unnecessary re-renders
          if (!current || current.width !== width || current.height !== height) {
            console.log(`Page ${pageNum} dimensions updated - width: ${width}, height: ${height}`);
            const newDimensions = {
              ...prev,
              [pageNum]: { width, height }
            };
            
            // Calculate the maximum content size (for first page or largest page)
            // Add padding for toolbar and footer
            const toolbarHeight = 48; // Approximate toolbar height
            const footerHeight = highlights.length > 0 ? 40 : 0; // Footer height if highlights exist
            const padding = 40; // Top and bottom padding (20px each)
            
            const maxWidth = Math.max(...Object.values(newDimensions).map(d => d.width));
            const maxHeight = Math.max(...Object.values(newDimensions).map(d => d.height));
            
            if (maxWidth > 0 && maxHeight > 0 && onContentSizeChange) {
              onContentSizeChange(
                maxWidth + 20, // Add some margin
                maxHeight + toolbarHeight + footerHeight + padding
              );
            }
            
            return newDimensions;
          }
          return prev;
        });
      }
    }
  };

  const onPageLoadSuccess = (page: any, pageNum: number) => {
    // Get the initial rendered page dimensions
    const { width, height } = page;
    setPageDimensions(prev => ({
      ...prev,
      [pageNum]: { width, height }
    }));
    console.log(`Page ${pageNum} loaded - width: ${width}, height: ${height}`);

    // Set up ResizeObserver to track when the page container size changes
    setTimeout(() => {
      const pageElement = pageRefs.current[pageNum];
      if (pageElement) {
        // Clean up existing observer for this page
        if (resizeObservers.current[pageNum]) {
          resizeObservers.current[pageNum]?.disconnect();
        }

        // Create ResizeObserver to watch for page container size changes
        const observer = new ResizeObserver(() => {
          // When container resizes, update dimensions from the actual canvas
          updatePageDimensions(pageNum);
        });

        observer.observe(pageElement);
        resizeObservers.current[pageNum] = observer;

        // Also check dimensions immediately in case canvas is already rendered
        updatePageDimensions(pageNum);
      }
    }, 100);
  };

  // Update page dimensions when scale changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      Object.keys(pageRefs.current).forEach(pageNumStr => {
        const pageNum = parseInt(pageNumStr, 10);
        if (pageRefs.current[pageNum]) {
          updatePageDimensions(pageNum);
        }
      });
    }, 100);
    
    return () => clearTimeout(timeoutId);
  }, [scale]);

  // Cleanup ResizeObservers on unmount
  useEffect(() => {
    return () => {
      Object.values(resizeObservers.current).forEach(observer => {
        observer?.disconnect();
      });
    };
  }, []);

  // Navigate to current highlight when it changes
  useEffect(() => {
    if (!highlights.length || currentHighlightIndex < 0) return;

    const currentHighlight = highlights[currentHighlightIndex];
    if (!currentHighlight) return;

    // Navigate to the page
    setCurrentPage(currentHighlight.page);

    // Scroll to the highlight
    setTimeout(() => {
      const pageElement = pageRefs.current[currentHighlight.page];
      if (pageElement) {
        pageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 300);
  }, [highlights, currentHighlightIndex]);

  // Zoom controls
  const zoomIn = () => {
    setScale(prev => Math.min(prev + 0.2, 3.0));
  };

  const zoomOut = () => {
    setScale(prev => Math.max(prev - 0.2, 0.5));
  };

  // Page navigation
  const previousPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const nextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, numPages));
  };

  // Get highlights for a specific page
  const getHighlightsForPage = (pageNum: number): Highlight[] => {
    return highlights.filter(h => h.page === pageNum);
  };

  // Render highlight overlays for a specific page
  const renderHighlights = (pageNum: number) => {
    const pageHighlights = getHighlightsForPage(pageNum);
    const dimensions = pageDimensions[pageNum];
    if (!pageHighlights.length || !dimensions || !dimensions.width || !dimensions.height) return null;

    return pageHighlights.map((highlight, index) => {
      const isActive = currentHighlightIndex !== undefined && 
                       highlights[currentHighlightIndex]?.id === highlight.id;
      
      // Calculate pixel positions from PDF point coordinates
      // The bounds are in PDF points (612x792 for standard letter size)
      // We need to scale them to the rendered page size
      const scaleX = dimensions.width / 612; // Standard letter width in points
      const scaleY = dimensions.height / 792; // Standard letter height in points
      
      const left = highlight.bounds.x * scaleX - 1;
      const top = highlight.bounds.y * scaleY - 1;
      const width = highlight.bounds.width * scaleX + 2;
      const height = highlight.bounds.height * scaleY + 2;

      return (
        <div
          key={highlight.id}
          style={{
            position: 'absolute',
            left: `${left}px`,
            top: `${top}px`,
            width: `${width}px`,
            height: `${height}px`,
            backgroundColor: 'rgba(255, 165, 0, 0.3)',
            pointerEvents: 'auto',
            cursor: 'pointer',
            transition: 'all 0.2s',
            zIndex: isActive ? 10 : 5,
          }}
          onClick={() => onHighlightClick && onHighlightClick(highlight)}
          title={highlight.phrase}
        />
      );
    });
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      {/* Compact toolbar */}
      <div
        style={{
          padding: '8px 12px',
          borderBottom: '1px solid #f0f0f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#fafafa',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Button 
            icon={<LeftOutlined />} 
            onClick={previousPage} 
            size="small"
            disabled={currentPage <= 1}
          />
          <span style={{ fontSize: '13px', color: '#666', minWidth: '80px', textAlign: 'center' }}>
            Page {currentPage} / {numPages || '...'}
          </span>
          <Button 
            icon={<RightOutlined />} 
            onClick={nextPage} 
            size="small"
            disabled={currentPage >= numPages}
          />
        </div>

        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          <Button icon={<ZoomOutOutlined />} onClick={zoomOut} size="small" disabled={scale <= 0.5} />
          <span style={{ fontSize: '12px', color: '#666', minWidth: '50px', textAlign: 'center' }}>
            {Math.round(scale * 100)}%
          </span>
          <Button icon={<ZoomInOutlined />} onClick={zoomIn} size="small" disabled={scale >= 3.0} />
        </div>
      </div>

      {/* PDF Viewer */}
      <div 
        ref={containerRef}
        style={{ 
          flex: 1, 
          overflow: 'auto', 
          position: 'relative',
          backgroundColor: '#525659',
          display: 'flex',
          justifyContent: 'center',
          padding: '20px 0'
        }}
      >
        <Document
          file={fileUrl}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              color: '#fff',
              padding: '20px'
            }}>
              Loading PDF...
            </div>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {Array.from(new Array(numPages), (el, index) => {
              const pageNum = index + 1;
              const isCurrentPage = pageNum === currentPage;
              
              return (
                <div
                  key={`page_${pageNum}`}
                  ref={(el) => { pageRefs.current[pageNum] = el; }}
                  style={{
                    position: 'relative',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                    backgroundColor: '#fff',
                    opacity: isCurrentPage ? 1 : 0.6,
                    transform: isCurrentPage ? 'scale(1)' : 'scale(0.95)',
                    transition: 'all 0.3s',
                  }}
                >
                  <Page
                    pageNumber={pageNum}
                    scale={scale}
                    onLoadSuccess={(page) => onPageLoadSuccess(page, pageNum)}
                    onRenderSuccess={() => {
                      // Also update dimensions when page re-renders (e.g., scale change)
                      updatePageDimensions(pageNum);
                    }}
                    renderTextLayer={true}
                    renderAnnotationLayer={true}
                  />
                  {/* Render highlights for this page */}
                  {renderHighlights(pageNum)}
                </div>
              );
            })}
          </div>
        </Document>
      </div>

      {/* Compact highlight info footer */}
      {highlights.length > 0 && (
        <div
          style={{
            padding: '8px 12px',
            borderTop: '1px solid #f0f0f0',
            backgroundColor: '#fffbf0',
            fontSize: '11px',
            borderLeft: '3px solid #FFA500',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            <span
              style={{
                background: 'rgba(255, 165, 0, 0.2)',
                padding: '2px 6px',
                borderRadius: '3px',
                fontWeight: 'bold',
                color: '#D97700',
                fontSize: '11px',
              }}
            >
              {highlights.length} highlight{highlights.length > 1 ? 's' : ''}
            </span>
            {highlights[currentHighlightIndex] && (
              <span style={{ color: '#888', fontSize: '10px' }}>
                "{highlights[currentHighlightIndex].phrase.substring(0, 40)}
                {highlights[currentHighlightIndex].phrase.length > 40 ? '...' : ''}"
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

PdfViewer.displayName = 'PdfViewer';

export default PdfViewer;
